/**
 * Standalone full-page legal-documents viewer (CWS-21215): one URL per
 * document type, opening straight to that document for the :country in the
 * path (see config/routes.rb / legal_documents_viewer_controller.rb). Every
 * document button already carries its own data (file path, name, location,
 * effective date, and its own URL) rendered server-side, so switching
 * between the two document types is a pure client-side pane swap — no fetch
 * involved. PDFs render via the browser's native <object>/<embed> viewer
 * (the same pattern as service_legal_agreements/_pdf_view.slim).
 *
 * Changing location is a full navigation (there is no single "browse this
 * location" URL to update in place); switching document type instead swaps
 * the pane and updates the URL via history.pushState so the address bar
 * always matches what's on screen without a page reload.
 *
 * Also reused, injected as a fragment, inside the in-app drawer
 * (legal_documents_drawer.js) — enableHistory: false there, since the
 * drawer's host page owns the address bar and must not have it rewritten
 * out from under an unrelated in-progress page (e.g. the signup form).
 * Changing location is a real navigation only on the standalone page;
 * the drawer instead passes onLocationChange to re-fetch and re-render
 * the new country's document inside itself, since a real navigation
 * there would defeat the whole point of not leaving the host page.
 */
const PDF_VIEW_HASH = 'toolbar=0&navpanes=0&view=FitH';

export const initLegalDocumentsViewer = (
  root = document.getElementById('legal-documents-viewer'),
  { enableHistory = true, onLocationChange, initialUrl } = {},
) => {
  if (!root) return;

  const menu = root.querySelector('#legal-documents-viewer-menu');
  const content = root.querySelector('#legal-documents-viewer-content');
  const backButton = root.querySelector('#legal-documents-viewer-back');
  const contentTitleEl = root.querySelector('#legal-documents-viewer-content-title');
  const contentSubtitleEl = root.querySelector('#legal-documents-viewer-content-subtitle');
  const downloadLink = root.querySelector('#legal-documents-viewer-download');
  const locationSelect = root.querySelector('#legal-documents-viewer-location');
  const cookiePolicyButton = root.querySelector('#legal-documents-viewer-cookie-policy');
  const pdfContainer = root.querySelector('#legal-documents-viewer-pdf-container');
  const declarationContainer = root.querySelector('#legal-documents-viewer-cookie-declaration');
  const loading = root.querySelector('#legal-documents-viewer-loading');
  const errorMessage = root.querySelector('#legal-documents-viewer-error');
  const docButtons = [...root.querySelectorAll('[data-legal-doc-type]')];
  const rowButtons = [...docButtons, cookiePolicyButton];
  const cookiePolicyName = cookiePolicyButton
    .querySelector('.cropwise-accounts-legal-viewer__item-label-name').textContent;

  let activeRowButton = null;
  // Tracks the active document's own viewer URL so the location select can
  // rebuild a destination from it. location.href works for that only when
  // enableHistory keeps it in sync with the viewer's own route; injected
  // into the drawer, location.href is still the host page's own URL, so
  // rewriting its last segment would build a broken, non-viewer route.
  // initialUrl (the drawer's own fetched href) seeds this for a country
  // with no current document of the requested type — no button opens to
  // overwrite it there, so without this seed, changing location away
  // from and back to that empty country would have no URL to rebuild
  // from and silently do nothing on the way back.
  let currentDocumentUrl = initialUrl
    ?? (enableHistory ? `${location.pathname}${location.search}` : null);
  // Bumped on every pane switch so an async Cookie Policy load/error handler
  // that resolves after the user has since opened a different pane can tell
  // it is stale and skip touching the shared loading/error elements.
  let paneGeneration = 0;

  // The back button and its own header live inside content-header, which is
  // only visible at all when .content itself is — no separate hidden
  // toggling needed here, just the menu/content pane swap.
  const showMenu = () => {
    paneGeneration += 1;
    menu.hidden = false;
    content.hidden = true;
    activeRowButton?.focus();
  };

  const enterContentView = (title, subtitle, { focus = true } = {}) => {
    menu.hidden = true;
    content.hidden = false;
    contentTitleEl.textContent = title;
    contentSubtitleEl.textContent = subtitle;
    if (focus) backButton.focus();
  };

  const markActiveRow = (activeButton) => {
    activeRowButton = activeButton;
    for (const button of rowButtons) {
      const isActive = button === activeButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive);
    }
  };

  // Mirrors service_legal_agreements/_pdf_view.slim's <object>/<embed> pair
  // (the browser's native PDF viewer renders the <object>, falling back to
  // the nested <embed> when it can't) instead of a JS PDF-rendering library.
  const buildPdfObject = (filePath) => {
    const url = new URL(filePath);
    url.hash = PDF_VIEW_HASH;
    const src = url.href;

    const embed = document.createElement('embed');
    embed.className = 'cropwise-accounts-legal-viewer__pdf-object';
    embed.type = 'application/pdf';
    embed.src = src;

    const object = document.createElement('object');
    object.className = 'cropwise-accounts-legal-viewer__pdf-object';
    object.type = 'application/pdf';
    object.data = src;
    object.append(embed);
    return object;
  };

  const docFromButton = (button) => ({
    filePath: button.dataset.filePath,
    name: button.dataset.name,
    locationName: button.dataset.locationName,
    effectiveDate: button.dataset.effectiveDate,
  });

  const showContent = (doc, { focus = true } = {}) => {
    const subtitle = [doc.locationName, doc.effectiveDate].filter(Boolean).join(' • ');
    enterContentView(doc.name, subtitle, { focus });
    downloadLink.hidden = false;
    downloadLink.href = doc.filePath;
    declarationContainer.hidden = true;
    declarationContainer.replaceChildren();
    loading.hidden = true;
    errorMessage.hidden = true;
    pdfContainer.hidden = false;
    pdfContainer.replaceChildren(buildPdfObject(doc.filePath));
  };

  const showCookieDeclaration = () => {
    enterContentView(cookiePolicyName, '');
    downloadLink.hidden = true;
    pdfContainer.hidden = true;
    pdfContainer.replaceChildren();
    declarationContainer.hidden = false;
    declarationContainer.replaceChildren();
    loading.hidden = false;
    errorMessage.hidden = true;
  };

  // The desktop two-pane layout keeps .content visually shown regardless of
  // its hidden attribute, so a stale document/title/PDF would otherwise
  // linger there once no row is active — e.g. navigating back past the
  // initial history entry for a country missing the requested type.
  const clearActiveDocument = () => {
    markActiveRow(null);
    contentTitleEl.textContent = '';
    contentSubtitleEl.textContent = '';
    downloadLink.hidden = true;
    pdfContainer.hidden = true;
    pdfContainer.replaceChildren();
    declarationContainer.hidden = true;
    declarationContainer.replaceChildren();
    loading.hidden = true;
    errorMessage.hidden = true;
  };

  // pushUrl is skipped for the initial render (the address bar already
  // matches), for popstate restores (the browser already changed it), and
  // whenever enableHistory is off (the drawer must not touch the host
  // page's address bar at all).
  // focus is skipped only for the initial render, so page load doesn't
  // steal focus from wherever the browser would otherwise put it.
  // The pushed/replaced URL keeps location.search (e.g. ?locale=de-AT) so a
  // refresh or share doesn't drop it, but the stored state.url stays
  // path-only, matching dataset.url, for the popstate lookup below.
  const openDocumentButton = (button, { pushUrl = true, focus = true } = {}) => {
    paneGeneration += 1;
    markActiveRow(button);
    showContent(docFromButton(button), { focus });
    currentDocumentUrl = enableHistory
      ? `${button.dataset.url}${location.search}`
      : button.dataset.url;
    if (pushUrl && enableHistory) {
      history.pushState({ url: button.dataset.url }, '', currentDocumentUrl);
    }
  };

  const openCookieDeclaration = async () => {
    paneGeneration += 1;
    const generation = paneGeneration;
    const isActive = () => generation === paneGeneration;
    markActiveRow(cookiePolicyButton);
    showCookieDeclaration();
    try {
      const { renderCookieDeclaration } = await import('./cookie_declaration');
      if (isActive()) renderCookieDeclaration({ isActive });
    } catch {
      if (isActive()) {
        loading.hidden = true;
        errorMessage.hidden = false;
      }
    }
  };

  for (const button of docButtons) {
    button.addEventListener('click', () => openDocumentButton(button));
  }

  cookiePolicyButton.addEventListener('click', openCookieDeclaration);
  backButton.addEventListener('click', showMenu);

  locationSelect.addEventListener('change', () => {
    if (!currentDocumentUrl) return;
    const url = new URL(currentDocumentUrl, location.origin);
    url.pathname = url.pathname.replace(/(?<country>[A-Z]{2})$/, locationSelect.value);
    if (onLocationChange) {
      onLocationChange(url.href);
    } else {
      location.href = url.href;
    }
  });

  if (enableHistory) {
    addEventListener('popstate', (event) => {
      const matchingButton = docButtons.find((button) => button.dataset.url === event.state?.url);
      if (matchingButton) {
        openDocumentButton(matchingButton, { pushUrl: false });
      } else {
        // showMenu runs first so it still has the previous activeRowButton
        // to focus — clearActiveDocument nulls that reference afterward.
        showMenu();
        clearActiveDocument();
        currentDocumentUrl = `${location.pathname}${location.search}`;
      }
    });
  }

  const initialButton = docButtons
    .find((button) => button.dataset.legalDocType === root.dataset.initialDocumentType);
  if (initialButton) {
    if (enableHistory) {
      const replacedUrl = `${initialButton.dataset.url}${location.search}`;
      history.replaceState({ url: initialButton.dataset.url }, '', replacedUrl);
    }
    openDocumentButton(initialButton, { pushUrl: false, focus: false });
  }
};
